import React, { useEffect, useState, useRef } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  orderBy,
  query,
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useParams, useNavigate } from "react-router-dom";
import styles from "../styles/BranchFeed.module.css";
import { ScrollReveal } from "../hooks/useScrollAnimation";

// Cloudinary configuration
const CLOUDINARY_UPLOAD_PRESET = "ml_default";
const CLOUDINARY_CLOUD_NAME = "dioeq3s5r";

// Upload helper
const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  formData.append("cloud_name", CLOUDINARY_CLOUD_NAME);
  formData.append("folder", "staco_branch_posts");

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    {
      method: "POST",
      body: formData,
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Upload failed");
  }

  const data = await response.json();
  return {
    url: data.secure_url,
    type: "image",
    public_id: data.public_id,
  };
};

const BRANCH_MAP = {
  "010": "CSE",
  "020": "AIML",
  "030": "DS",
  "040": "CS",
  "050": "IT",
  "060": "IoT",
};

const REVERSE_BRANCH_MAP = Object.entries(BRANCH_MAP).reduce((acc, [code, name]) => {
  acc[name.toUpperCase()] = code;
  return acc;
}, {});

function resolveBranch(param) {
  if (!param) return { code: "000", name: "Unknown" };

  const normalized = param.toUpperCase();

  // If param is a name (CSE), return code (010)
  if (REVERSE_BRANCH_MAP[normalized]) {
    return { code: REVERSE_BRANCH_MAP[normalized], name: normalized };
  }

  // If param is a code (010), return name (CSE)
  if (BRANCH_MAP[param]) {
    return { code: param, name: BRANCH_MAP[param] };
  }

  return { code: param, name: "Unknown Branch" };
}

function extractBranchCode(email) {
  if (!email) return null;
  if (!email.endsWith('@mallareddyuniversity.ac.in')) return null;
  const username = email.split('@')[0];
  if (username.length >= 9) {
    const branchCode = username.substring(6, 9);
    if (/^\d{3}$/.test(branchCode)) {
      return branchCode;
    }
  }
  return null;
}

const timeSince = (date) => {
  if (!date) return "Just now";
  const now = new Date();
  const posted = date.toDate ? date.toDate() : new Date(date);
  const seconds = Math.floor((now - posted) / 1000);

  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) return interval + "y";
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return interval + "mo";
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return interval + "d";
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return interval + "h";
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return interval + "m";
  return "Just now";
};

export default function BranchFeed() {
  const { branchCode } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [user, setUser] = useState(null);
  const [userMap, setUserMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [replyText, setReplyText] = useState({});
  const [expandedPosts, setExpandedPosts] = useState({});
  const [editMode, setEditMode] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [sortBy, setSortBy] = useState("newest");
  const [showReplyInput, setShowReplyInput] = useState({});



  const { code: activeBranchCode, name: branchName } = resolveBranch(branchCode);
  // Use resolved code for DB queries to ensure consistency (e.g. always '010', never 'CSE')
  const dbBranchCode = activeBranchCode;

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (authUser) => {
      try {
        setUser(authUser);
        setLoading(true);

        if (authUser?.email) {
          if (!authUser.email.endsWith('@mallareddyuniversity.ac.in')) {
            setAuthorized(false);
            setError("Only MRU email accounts can access this content");
            setLoading(false);
            return;
          }

          setAuthorized(true);
          setError(null);

          const unsubscribeUsers = onSnapshot(collection(db, "users"), (snapshot) => {
            const map = {};
            snapshot.forEach((doc) => {
              const data = doc.data();
              map[data.email] = {
                username: data.username || data.email.split('@')[0],
                avatar: data.avatar,
                rollNumber: data.email.split('@')[0]
              };
            });
            setUserMap(map);
          });

          const postsRef = collection(db, "branch_posts", dbBranchCode, "posts");
          const q = query(postsRef, orderBy("createdAt", "desc"));

          const unsubscribePosts = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt,
            }));
            setPosts(data);
            setLoading(false);
          }, (error) => {
            console.error("Error fetching posts:", error);
            setError("Failed to load posts");
            setLoading(false);
          });

          return () => {
            unsubscribeUsers();
            unsubscribePosts();
          };
        } else {
          setAuthorized(false);
          setError("Please login with your MRU email to continue.");
          setLoading(false);
        }
      } catch (err) {
        console.error("Authentication error:", err);
        setError("An error occurred during authentication");
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, [dbBranchCode, branchName]);

  const handlePost = async () => {
    if (!newPost.trim() && files.length === 0 && existingFiles.length === 0) {
      setError("Post cannot be empty");
      return;
    }

    if (!user?.email) {
      setError("You must be logged in to post");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploadedFiles = [];
      for (const file of files) {
        const uploaded = await uploadToCloudinary(file);
        uploadedFiles.push(uploaded);
      }

      const finalFiles = [...existingFiles, ...uploadedFiles];

      if (editMode) {
        await updateDoc(doc(db, "branch_posts", dbBranchCode, "posts", editMode), {
          text: newPost,
          files: finalFiles,
          updatedAt: serverTimestamp(),
        });
        setEditMode(null);
      } else {
        await addDoc(collection(db, "branch_posts", dbBranchCode, "posts"), {
          text: newPost,
          files: finalFiles,
          author: user.email,
          authorUid: user.uid,
          authorName: userMap[user.email]?.username || user.email.split('@')[0],
          createdAt: serverTimestamp(),
          likes: [],
          comments: [],
          reports: [],
          branchCode: dbBranchCode,
        });
      }

      setNewPost("");
      setFiles([]);
      setExistingFiles([]);
      setIsUploading(false);
    } catch (err) {
      console.error("Error posting:", err);
      setError(err.message || "Failed to create post");
      setIsUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    const newFiles = [...files];
    let imageCount = newFiles.length + existingFiles.length;

    for (const file of selected) {
      if (file.type.startsWith("image/")) {
        if (imageCount >= 5) {
          setError("Maximum 5 images allowed per post");
          break;
        }
        if (file.size > 5 * 1024 * 1024) {
          setError("Image size must be less than 5MB");
          continue;
        }
        imageCount++;
        newFiles.push(file);
      } else {
        setError(`Unsupported file type: ${file.type}. Only images allowed.`);
      }
    }

    setFiles(newFiles.slice(0, 5 - existingFiles.length));
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const removeExistingFile = (index) => {
    setExistingFiles(existingFiles.filter((_, i) => i !== index));
  };

  const toggleLike = async (postId, likes = []) => {
    if (!user) {
      setError("Please login to like posts");
      return;
    }

    try {
      const postRef = doc(db, "branch_posts", dbBranchCode, "posts", postId);
      const hasLiked = likes.includes(user.uid);

      await updateDoc(postRef, {
        likes: hasLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      });
    } catch (err) {
      console.error("Error toggling like:", err);
      setError("Failed to update like");
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      await deleteDoc(doc(db, "branch_posts", dbBranchCode, "posts", postId));
    } catch (err) {
      console.error("Error deleting post:", err);
      setError("Failed to delete post");
    }
  };

  const handleEdit = (post) => {
    setNewPost(post.text || "");
    setFiles([]);
    setExistingFiles(post.files || []);
    setEditMode(post.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addComment = async (postId) => {
    const comment = replyText[postId]?.trim();
    if (!comment || !user) {
      setError("Please write a comment");
      return;
    }

    try {
      const postRef = doc(db, "branch_posts", dbBranchCode, "posts", postId);
      const newComment = {
        id: Date.now().toString(),
        text: comment,
        author: user.email,
        authorName: userMap[user.email]?.username || user.email.split('@')[0],
        createdAt: new Date().toISOString(),
      };

      await updateDoc(postRef, {
        comments: arrayUnion(newComment),
      });

      setReplyText(prev => ({ ...prev, [postId]: "" }));
      setShowReplyInput(prev => ({ ...prev, [postId]: false }));
    } catch (err) {
      console.error("Error adding comment:", err);
      setError("Failed to add comment");
    }
  };

  const toggleComments = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  const sortedPosts = [...posts].sort((a, b) => {
    const timeA = a.createdAt?.seconds || 0;
    const timeB = b.createdAt?.seconds || 0;

    if (sortBy === "newest") return timeB - timeA;
    if (sortBy === "oldest") return timeA - timeB;
    if (sortBy === "popular") return (b.likes?.length || 0) - (a.likes?.length || 0);
    return timeB - timeA;
  });

  const handleCancelEdit = () => {
    setEditMode(null);
    setNewPost("");
    setFiles([]);
    setExistingFiles([]);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Just now";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={styles.feedContainer}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div> {/* Minimal generic spinner if global styles have it, or remove */}
          <p>Loading {branchName} branch feed...</p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className={styles.feedContainer}>
        <div style={{ textAlign: 'center', padding: '40px', background: 'var(--color-bg-card)', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
          <h2>🔒 Access Restricted</h2>
          <p style={{ color: 'red', margin: '20px 0' }}>{error}</p>
          <p>
            <strong>Branch:</strong> {branchName} ({branchCode})
          </p>
          {user && (
            <div style={{ margin: '20px 0' }}>
              <p><strong>Logged in as:</strong> {user.email}</p>
              <p><strong>Your branch code:</strong> {extractBranchCode(user.email)}</p>
            </div>
          )}
          <button
            className={styles.postBtn}
            onClick={() => navigate(-1)}
            style={{ marginTop: '20px' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.feedContainer}>
      {/* Branch Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1>{branchName} Branch</h1>
          <span className={styles.branchCode}>Branch Code: {branchCode}</span>
          <div className={styles.branchStats}>
            {posts.length} posts • {new Set(posts.map(p => p.author)).size} active students
          </div>
        </div>
        <div className={styles.branchBadge}>
          <span>MRU</span>
          <span>{branchCode}</span>
        </div>
      </div>

      {/* Create/Edit Post */}
      <div className={styles.createPost}>
        <h3>{editMode ? "✏️ Edit Post" : "📝 Create a Post"}</h3>
        <textarea
          className={styles.textarea}
          rows="4"
          value={newPost}
          onChange={(e) => setNewPost(e.target.value)}
          placeholder={`Share something in ${branchName} branch...`}
          maxLength="1000"
        />

        {/* File Upload Area */}
        <div className={styles.fileUploadArea}>
          <label className={styles.uploadLabel}>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            <span style={{ fontSize: '1.2rem' }}>📷</span>
            <span>Add Images (max 5)</span>
          </label>

          {/* File Previews */}
          {(files.length > 0 || existingFiles.length > 0) && (
            <div className={styles.filePreviews}>
              {existingFiles.map((file, index) => (
                <div key={`existing-${index}`} className={styles.filePreview}>
                  <img src={file.url} alt={`Existing ${index + 1}`} />
                  <button
                    className={styles.removeFileBtn}
                    onClick={() => removeExistingFile(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {files.map((file, index) => (
                <div key={`new-${index}`} className={styles.filePreview}>
                  <img src={URL.createObjectURL(file)} alt={`New ${index + 1}`} />
                  <button
                    className={styles.removeFileBtn}
                    onClick={() => removeFile(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.postActions}>
          <div className={styles.charCount}>
            {newPost.length}/1000 characters
          </div>
          <div className={styles.actionButtons}>
            {editMode && (
              <button
                className={styles.cancelBtn}
                onClick={handleCancelEdit}
                disabled={isUploading}
              >
                Cancel
              </button>
            )}
            <button
              className={styles.postBtn}
              onClick={handlePost}
              disabled={isUploading || (!newPost.trim() && files.length === 0 && existingFiles.length === 0)}
            >
              {isUploading ? "Posting..." : editMode ? "Update Post" : "Post to Branch"}
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ background: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '4px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#c62828' }}>×</button>
        </div>
      )}

      {/* Posts Filter/Sort */}
      <div className={styles.postsFilter}>
        <div className={styles.sortOptions}>
          <span>Sort by:</span>
          <button
            className={`${styles.sortBtn} ${sortBy === 'newest' ? styles.sortBtnActive : ''}`}
            onClick={() => setSortBy('newest')}
          >
            Newest
          </button>
          <button
            className={`${styles.sortBtn} ${sortBy === 'popular' ? styles.sortBtnActive : ''}`}
            onClick={() => setSortBy('popular')}
          >
            Most Liked
          </button>
          <button
            className={`${styles.sortBtn} ${sortBy === 'oldest' ? styles.sortBtnActive : ''}`}
            onClick={() => setSortBy('oldest')}
          >
            Oldest
          </button>
        </div>
        <div>
          {sortedPosts.length} {sortedPosts.length === 1 ? 'post' : 'posts'}
        </div>
      </div>

      {/* Posts List */}


      {/* Posts List */}
      <div className={styles.postsList}>
        {sortedPosts.length === 0 ? (
          <ScrollReveal animation="animate-fade-in-up">
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div style={{ fontSize: '3rem', marginBottom: '20px', opacity: '0.3' }}>📝</div>
              <h3>No posts yet in {branchName} branch</h3>
              <p>Be the first to share something with your branch mates!</p>
            </div>
          </ScrollReveal>
        ) : (
          sortedPosts.map((post, index) => (
            <ScrollReveal
              key={post.id}
              delay={index < 5 ? index * 100 : 0} // Stagger first 5 posts
              animation="animate-fade-in-up"
            >
              <div className={styles.post}>
                {/* ... Post Content ... */}
                {/* Post Header */}
                <div className={styles.postHeader}>
                  <div
                    className={styles.authorInfo}
                    onClick={() => navigate(`/profile/${encodeURIComponent(post.author)}`)}
                  >
                    {userMap[post.author]?.avatar ? (
                      <img
                        src={userMap[post.author].avatar}
                        alt={userMap[post.author]?.username}
                        className={styles.authorAvatar}
                      />
                    ) : (
                      <div className={styles.placeholderAvatar}>
                        {userMap[post.author]?.username?.[0]?.toUpperCase() || post.author[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className={styles.authorName}>
                        {userMap[post.author]?.username || post.author.split('@')[0]}
                      </div>
                      <div className={styles.postTime}>
                        {timeSince(post.createdAt)} • {formatDate(post.createdAt)}
                      </div>
                    </div>
                  </div>

                  {/* Post Actions Menu */}
                  {post.authorUid === user?.uid && (
                    <div>
                      <button
                        className={styles.menuBtn}
                        onClick={() => handleEdit(post)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className={styles.menuBtn}
                        onClick={() => handleDelete(post.id)}
                        title="Delete"
                        style={{ color: '#d32f2f' }}
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>

                {/* Post Content */}
                <div className={styles.postContent}>
                  {post.text && <p className={styles.postText}>{post.text}</p>}

                  {/* Post Images */}
                  {post.files?.length > 0 && (
                    <div className={`${styles.postImages} ${post.files.length > 1 ? styles.grid : ''}`}>
                      {post.files.slice(0, 4).map((file, index) => (
                        <div key={index} style={{ position: 'relative', width: '100%', height: '100%' }}>
                          <img
                            src={file.url}
                            alt={`Post attachment ${index + 1}`}
                            className={styles.postImage}
                            onClick={() => window.open(file.url, '_blank')}
                          />
                        </div>
                      ))}
                      {post.files.length > 4 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eee', color: '#666', fontWeight: 'bold' }}>
                          +{post.files.length - 4} more
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Post Stats */}
                <div className={styles.postStats}>
                  <span>
                    ❤️ {post.likes?.length || 0} likes
                  </span>
                  <span>
                    💬 {post.comments?.length || 0} comments
                  </span>
                </div>

                {/* Post Actions */}
                <div className={styles.postActionsBar}>
                  <button
                    className={`${styles.actionBtn} ${post.likes?.includes(user?.uid) ? styles.liked : ''}`}
                    onClick={() => toggleLike(post.id, post.likes || [])}
                  >
                    ❤️ Like
                  </button>
                  <button
                    className={styles.actionBtn}
                    onClick={() => setShowReplyInput(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                  >
                    💬 Comment
                  </button>
                  <button
                    className={styles.actionBtn}
                    onClick={() => toggleComments(post.id)}
                  >
                    {expandedPosts[post.id] ? 'Hide Comments' : 'Show Comments'}
                  </button>
                </div>

                {/* Comment Input */}
                {showReplyInput[post.id] && (
                  <div className={`${styles.commentInputArea} animate-fade-in`}>
                    <textarea
                      className={styles.commentTextarea}
                      rows="1"
                      placeholder="Write a comment..."
                      value={replyText[post.id] || ""}
                      onChange={(e) => setReplyText(prev => ({ ...prev, [post.id]: e.target.value }))}
                      maxLength="500"
                    />
                    <button
                      className={styles.commentPostBtn}
                      onClick={() => addComment(post.id)}
                      disabled={!replyText[post.id]?.trim()}
                    >
                      Post
                    </button>
                  </div>
                )}

                {/* Comments Section */}
                {expandedPosts[post.id] && (
                  <div className={`${styles.commentsSection} animate-fade-in`}>
                    {post.comments?.length > 0 ? (
                      post.comments.map((comment, index) => (
                        <div key={comment.id || index} className={styles.comment}>
                          <div className={styles.commentHeader}>
                            <span className={styles.commentAuthor}>
                              {comment.authorName || comment.author.split('@')[0]}
                            </span>
                            <span className={styles.commentTime}>
                              {timeSince({ toDate: () => new Date(comment.createdAt) })}
                            </span>
                          </div>
                          <p className={styles.commentBody}>{comment.text}</p>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#666', fontSize: '0.9rem', fontStyle: 'italic', textAlign: 'center' }}>No comments yet.</p>
                    )}
                  </div>
                )}
              </div>
            </ScrollReveal>
          ))
        )}
      </div>
    </div>
  );
}